import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useCurrentUserId } from '@/modules/time-attendance/time-tracker/hooks/use-my-timesheet';
import { BalancesCard } from '../../components/balances-card/balances-card';
import { HomeHero } from '../../components/home-hero/home-hero';
import { LunchCard } from '../../components/lunch-card/lunch-card';
import { NeedsAttentionCard } from '../../components/needs-attention-card/needs-attention-card';
import {
  PendingApprovalsCard,
  useReviewQueues,
} from '../../components/pending-approvals-card/pending-approvals-card';
import { ThisWeekCard } from '../../components/this-week-card/this-week-card';
import { UpcomingHolidaysCard } from '../../components/upcoming-holidays-card/upcoming-holidays-card';
import { YourRequestsCard } from '../../components/your-requests-card/your-requests-card';

const PERM = HRM_PERMISSIONS;

/**
 * Home (HR Module Prototype, Component Map › Home): the hero band, then a bento grid of four equal
 * columns. Row 1: Needs your attention (two columns), Lunch, Balances; row 2: This week, Your requests,
 * Pending approvals, Upcoming holidays. Two columns on narrower screens, one on phones; cards in a row
 * share its height. Each card shows only for those who may open the page it summarizes, so a missing
 * one lets the others move up.
 */
export const HomePage = () => {
  const userId = useCurrentUserId();
  const canUseAttendance = useHasPermission(PERM.TIME_ATTENDANCE.VIEW_ATTENDANCE);
  const canLunch = useHasPermission(PERM.MEAL.LUNCH_VIEW_OWN);
  const canTimeOff = useHasPermission(PERM.TIME_OFF.VIEW_OWN);
  const queues = useReviewQueues();
  const canReview = Object.values(queues).some(Boolean);

  return (
    // A shorter bottom margin than other pages (`!pb-6` over the layout's pb-16), so Home fits a desktop
    // screen without scrolling; smaller screens still scroll.
    <div className="flex w-full flex-col gap-4 !pb-6">
      <HomeHero canUseAttendance={canUseAttendance} />

      <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 min-[1250px]:grid-cols-4">
        <NeedsAttentionCard userId={userId} />
        {canLunch && <LunchCard />}
        {canTimeOff && <BalancesCard userId={userId} />}
        {/* Worked time comes from Attendance (2 Nov 2026), so the chart goes with that permission. */}
        {canUseAttendance && <ThisWeekCard userId={userId} />}
        <YourRequestsCard userId={userId} />
        {canReview && <PendingApprovalsCard queues={queues} />}
        <UpcomingHolidaysCard />
      </div>
    </div>
  );
};
